import { safeTeleportTarget, type World } from '../sim/world';
import type { Vessel } from '../sim/vessel';
import { DROP_RANGE } from '../sim/encounters';
import { KNOTS_PER_MS, METRES_PER_NM, bearingText, bearingTo, nmText } from './nautical';

// Dev chart: a plan of the sea round our ship with every vessel on it (seen
// by our lookouts or not). Clicking on it moves the ship there, keeping her
// heading and way; the move goes to the sim as a command.

/** Half-widths of the chart, sea miles, and the spacing of its range rings. */
const RANGES: readonly [number, number][] = [[0.5, 0.1], [1, 0.25], [2, 0.5], [5, 1], [10, 2], [20, 5]];

const COLOURS: Record<string, string> = { player: '#e6bd6a', navy: '#9cc3f0', pirate: '#e57a5c', merchant: '#d8cda8' };
const INK = 'rgba(233, 226, 207, ';
const SANS = "'Segoe UI', system-ui, -apple-system, sans-serif";

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, html?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

/** A ship in plan, bow along -y (north), `s` long from the stern to the bow. */
function hullPath(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.55);
  ctx.quadraticCurveTo(s * 0.26, -s * 0.2, s * 0.2, s * 0.45);
  ctx.lineTo(-s * 0.2, s * 0.45);
  ctx.quadraticCurveTo(-s * 0.26, -s * 0.2, 0, -s * 0.55);
  ctx.closePath();
}

export class DevMap {
  readonly el: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly rangeEl: HTMLElement;
  private readonly readout: HTMLElement;
  private range = 3;
  /** Pointer over the chart, CSS pixels from its top left. */
  private cursor: { x: number; y: number } | null = null;
  /** The ship under the pointer, if any (a click then comes up alongside her). */
  private hover: Vessel | null = null;
  private px = 0;
  private dpr = 1;

  constructor(private readonly world: World, private readonly onMove: (x: number, z: number) => void) {
    this.el = el('div', 'panel devmap hidden');
    const head = el('div', 'devmap-head');
    head.append(el('h2', '', 'Chart <span>dev · click to move the ship</span>'));
    const zoom = el('div', 'devmap-zoom');
    const wider = el('button', '', '−');
    const closer = el('button', '', '+');
    wider.title = 'Show more sea';
    closer.title = 'Closer in';
    this.rangeEl = el('span');
    wider.addEventListener('click', () => this.zoom(1));
    closer.addEventListener('click', () => this.zoom(-1));
    zoom.append(wider, this.rangeEl, closer);
    const close = el('button', 'devmap-close', '×');
    close.title = 'Close (Esc)';
    close.addEventListener('click', () => this.close());
    head.append(zoom, close);

    this.canvas = el('canvas', 'devmap-canvas');
    this.ctx = this.canvas.getContext('2d')!;
    const foot = el('div', 'devmap-foot');
    this.readout = el('div', 'devmap-readout');
    const key = (c: string, t: string, hollow = false) =>
      `<span><i style="${hollow ? `border:1.5px solid ${c}` : `background:${c}`}"></i>${t}</span>`;
    const legend = el('div', 'devmap-legend', [
      key(COLOURS.player!, 'Ours'), key(COLOURS.navy!, 'Navy'), key(COLOURS.pirate!, 'Pirate'),
      key(COLOURS.merchant!, 'Merchant'), key(`${INK}0.7)`, 'Not in sight', true),
    ].join(''));
    foot.append(this.readout, legend);
    this.el.append(head, this.canvas, foot);

    this.canvas.addEventListener('pointermove', (e) => {
      const r = this.canvas.getBoundingClientRect();
      this.cursor = { x: e.clientX - r.left, y: e.clientY - r.top };
    });
    this.canvas.addEventListener('pointerleave', () => (this.cursor = null));
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.zoom(e.deltaY > 0 ? 1 : -1);
    }, { passive: false });
    this.canvas.addEventListener('click', (e) => {
      // On a ship: make for her (the sim keeps us a safe distance off);
      // a glyph covers hundreds of metres of sea when zoomed out.
      if (this.hover && !this.hover.ship.sunk) return this.onMove(this.hover.ship.com.x, this.hover.ship.com.z);
      const r = this.canvas.getBoundingClientRect();
      const p = this.toWorld(e.clientX - r.left, e.clientY - r.top);
      this.onMove(p.x, p.z);
    });
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
    this.zoom(0);
  }

  get isOpen(): boolean {
    return !this.el.classList.contains('hidden');
  }

  open(): void {
    this.el.classList.remove('hidden');
  }

  close(): void {
    this.el.classList.add('hidden');
    this.cursor = null;
    this.hover = null;
  }

  private zoom(d: number): void {
    this.range = Math.max(0, Math.min(RANGES.length - 1, this.range + d));
    this.rangeEl.textContent = `${RANGES[this.range]![0]} nm`;
  }

  /** Pixels per metre, and the chart centre (our ship) in pixels. */
  private get scale(): number {
    return this.px / 2 / (RANGES[this.range]![0] * METRES_PER_NM);
  }

  private toWorld(x: number, y: number): { x: number; z: number } {
    const s = this.world.player.ship.com, k = this.scale, c = this.px / 2;
    return { x: s.x + (x - c) / k, z: s.z + (y - c) / k };
  }

  private toScreen(x: number, z: number): [number, number] {
    const s = this.world.player.ship.com, k = this.scale, c = this.px / 2;
    return [c + (x - s.x) * k, c + (z - s.z) * k];
  }

  /** Redraw (every frame while open: the ships are moving). */
  draw(): void {
    if (!this.isOpen) return;
    const css = this.canvas.clientWidth;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (css !== this.px || dpr !== this.dpr) {
      this.px = css;
      this.dpr = dpr;
      this.canvas.width = Math.round(css * dpr);
      this.canvas.height = Math.round(css * dpr);
    }
    if (this.px < 10) return;
    const ctx = this.ctx, S = this.px, c = S / 2, k = this.scale;
    const w = this.world, me = w.player;
    const [range, step] = RANGES[this.range]!;
    const stepM = step * METRES_PER_NM;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Sea.
    const bg = ctx.createRadialGradient(c, c, 0, c, c, S * 0.75);
    bg.addColorStop(0, '#173b50');
    bg.addColorStop(1, '#0a1b27');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, S, S);

    // A grid fixed to the sea (so her way shows), and range rings on her.
    const ox = me.ship.com.x, oz = me.ship.com.z, half = range * METRES_PER_NM;
    ctx.lineWidth = 1;
    ctx.strokeStyle = `${INK}0.05)`;
    ctx.beginPath();
    for (let gx = Math.ceil((ox - half) / stepM) * stepM; gx <= ox + half; gx += stepM) {
      const x = c + (gx - ox) * k;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, S);
    }
    for (let gz = Math.ceil((oz - half) / stepM) * stepM; gz <= oz + half; gz += stepM) {
      const y = c + (gz - oz) * k;
      ctx.moveTo(0, y);
      ctx.lineTo(S, y);
    }
    ctx.stroke();
    ctx.font = `10px ${SANS}`;
    ctx.textAlign = 'center';
    for (let r = step; r <= range * 1.42; r += step) {
      const rp = r * METRES_PER_NM * k;
      ctx.strokeStyle = `${INK}${Number.isInteger(r / (step * 2)) ? 0.16 : 0.09})`;
      ctx.beginPath();
      ctx.arc(c, c, rp, 0, Math.PI * 2);
      ctx.stroke();
      if (c - rp > 12) {
        ctx.fillStyle = `${INK}0.4)`;
        ctx.fillText(`${r} nm`, c, c - rp - 3);
      }
    }

    // How far the lookouts can see today.
    const vis = w.visibility * k;
    ctx.save();
    ctx.setLineDash([4, 5]);
    ctx.strokeStyle = 'rgba(143, 191, 136, 0.45)';
    ctx.beginPath();
    ctx.arc(c, c, vis, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    if (vis < S * 0.7) {
      ctx.fillStyle = 'rgba(143, 191, 136, 0.7)';
      ctx.textAlign = 'left';
      ctx.fillText(`visibility ${nmText(w.visibility)}`, c + vis * 0.72 + 4, c + vis * 0.72 + 12);
    }

    // Other ships, nearest first so they get their labels when ships in
    // company crowd together (the readout names any ship pointed at).
    let hover: Vessel | null = null;
    let hoverD = 14;
    const labels: { x: number; y: number; w: number; h: number }[] = [{ x: c - 16, y: c - 16, w: 32, h: 32 }];
    // Ships the move would leave so far behind that they're dropped.
    const aim = this.cursor ? this.toWorld(this.cursor.x, this.cursor.y) : null;
    const lost = new Set(aim ? w.encounters.droppedFrom(aim.x, aim.z) : []);
    const others = w.vessels.filter((v) => v !== me)
      .sort((a, b2) => Math.hypot(a.ship.com.x - ox, a.ship.com.z - oz) - Math.hypot(b2.ship.com.x - ox, b2.ship.com.z - oz));
    for (const v of others) {
      const [x, y] = this.toScreen(v.ship.com.x, v.ship.com.z);
      const sunk = v.ship.sunk;
      const colour = sunk ? '#7d7a72' : COLOURS[v.faction] ?? '#ccc';
      if (x < 0 || y < 0 || x > S || y > S) {
        this.edgeMarker(x, y, colour, v);
        continue;
      }
      if (this.cursor) {
        const d = Math.hypot(this.cursor.x - x, this.cursor.y - y);
        if (d < hoverD) (hover = v), (hoverD = d);
      }
      const seen = w.lookout.get(v.id)?.visible ?? false;
      this.ship(x, y, v.ship.heading, 6 + 4 * v.ship.sx, colour, seen && !sunk, v.ship.vel.x, v.ship.vel.z, k);
      if (lost.has(v)) {
        ctx.save();
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = 'rgba(217, 105, 76, 0.9)';
        ctx.beginPath();
        ctx.arc(x, y, 13, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      const status = sunk ? ' (sunk)' : v.state === 'active' ? '' : ` (${v.state})`;
      const name = `${v.name}${status}`;
      ctx.font = `11px ${SANS}`;
      const lw = Math.max(ctx.measureText(name).width, ctx.measureText(v.cls.name).width * 0.92);
      const box = { x: x + 9, y: y - 12, w: lw + 2, h: 26 };
      if (labels.some((r) => box.x < r.x + r.w && box.x + box.w > r.x && box.y < r.y + r.h && box.y + box.h > r.y)) continue;
      labels.push(box);
      ctx.fillStyle = colour;
      ctx.textAlign = 'left';
      ctx.fillText(name, x + 10, y - 2);
      ctx.fillStyle = `${INK}0.5)`;
      ctx.font = `10px ${SANS}`;
      ctx.fillText(v.cls.name, x + 10, y + 10);
    }

    // Us.
    this.ship(c, c, me.ship.heading, 13, COLOURS.player!, true, me.ship.vel.x, me.ship.vel.z, k);

    // North, the wind, the scale.
    ctx.textAlign = 'center';
    ctx.fillStyle = `${INK}0.75)`;
    ctx.font = `12px ${SANS}`;
    ctx.fillText('N', S - 20, 22);
    ctx.strokeStyle = `${INK}0.75)`;
    ctx.beginPath();
    ctx.moveTo(S - 20, 28);
    ctx.lineTo(S - 20, 46);
    ctx.moveTo(S - 24, 33);
    ctx.lineTo(S - 20, 27);
    ctx.lineTo(S - 16, 33);
    ctx.stroke();
    this.windRose(30, 30);
    const bar = stepM * k;
    ctx.strokeStyle = `${INK}0.7)`;
    ctx.beginPath();
    ctx.moveTo(14, S - 16);
    ctx.lineTo(14 + bar, S - 16);
    ctx.moveTo(14, S - 20);
    ctx.lineTo(14, S - 12);
    ctx.moveTo(14 + bar, S - 20);
    ctx.lineTo(14 + bar, S - 12);
    ctx.stroke();
    ctx.fillStyle = `${INK}0.7)`;
    ctx.textAlign = 'left';
    ctx.font = `10px ${SANS}`;
    ctx.fillText(`${step} nm`, 18 + bar, S - 12);

    // Where a click would put her.
    this.hover = hover;
    this.readout.innerHTML = this.cursorInfo(hover, [...lost]);
  }

  /** A ship glyph with a leader for two minutes of her way. */
  private ship(x: number, y: number, heading: number, size: number, colour: string, filled: boolean, vx: number, vz: number, k: number): void {
    const ctx = this.ctx;
    const lead = Math.hypot(vx, vz) * 120 * k;
    if (lead > 4) {
      ctx.strokeStyle = colour;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + vx * 120 * k, y + vz * 120 * k);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(heading);
    hullPath(ctx, size * 2);
    if (filled) {
      ctx.fillStyle = colour;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    } else {
      ctx.strokeStyle = colour;
      ctx.lineWidth = 1.5;
    }
    ctx.stroke();
    ctx.restore();
    ctx.lineWidth = 1;
  }

  /** A ship off the chart: an arrow on its edge pointing to her, with her range. */
  private edgeMarker(x: number, y: number, colour: string, v: Vessel): void {
    const ctx = this.ctx, S = this.px, c = S / 2;
    const dx = x - c, dy = y - c;
    const t = (c - 14) / Math.max(Math.abs(dx), Math.abs(dy));
    const ex = c + dx * t, ey = c + dy * t;
    const a = Math.atan2(dy, dx);
    ctx.save();
    ctx.translate(ex, ey);
    ctx.rotate(a);
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.moveTo(7, 0);
    ctx.lineTo(-5, -5);
    ctx.lineTo(-5, 5);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    const me = this.world.player.ship.com;
    ctx.fillStyle = colour;
    ctx.font = `10px ${SANS}`;
    ctx.textAlign = ex > c + 1 ? 'right' : ex < c - 1 ? 'left' : 'center';
    const ly = ey + (ey > c ? -10 : 16);
    const lx = ex + (ex > c + 1 ? -10 : ex < c - 1 ? 10 : 0);
    ctx.fillText(`${v.name} · ${nmText(Math.hypot(v.ship.com.x - me.x, v.ship.com.z - me.z))}`, lx, ly);
  }

  /** Wind indicator: an arrow flying downwind, with speed and where it's from. */
  private windRose(x: number, y: number): void {
    const ctx = this.ctx, wind = this.world.wind;
    ctx.strokeStyle = `${INK}0.35)`;
    ctx.beginPath();
    ctx.arc(x, y, 16, 0, Math.PI * 2);
    ctx.stroke();
    const to = wind.fromBearing + Math.PI;
    const fx = Math.sin(to), fy = -Math.cos(to);
    ctx.strokeStyle = `${INK}0.85)`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x - fx * 12, y - fy * 12);
    ctx.lineTo(x + fx * 12, y + fy * 12);
    ctx.lineTo(x + fx * 6 - fy * 4, y + fy * 6 + fx * 4);
    ctx.moveTo(x + fx * 12, y + fy * 12);
    ctx.lineTo(x + fx * 6 + fy * 4, y + fy * 6 - fx * 4);
    ctx.stroke();
    ctx.lineWidth = 1;
    ctx.fillStyle = `${INK}0.75)`;
    ctx.font = `10px ${SANS}`;
    ctx.textAlign = 'left';
    ctx.fillText(`${Math.round(wind.speed * KNOTS_PER_MS)} kn`, x + 22, y - 2);
    ctx.fillText(`from ${bearingText(wind.fromBearing).split(' ')[0]}`, x + 22, y + 10);
  }

  /** The readout under the chart, and the pointer's mark on it. */
  private cursorInfo(hover: Vessel | null, lost: Vessel[]): string {
    const w = this.world, me = w.player.ship.com;
    if (!this.cursor) {
      const n = w.vessels.filter((v) => v !== w.player && !v.ship.sunk).length;
      return `${n === 0 ? 'No other ships at sea.' : `${n} other ship${n === 1 ? '' : 's'} at sea.`} Point at the chart; scroll to zoom.`;
    }
    const p = hover ? { x: hover.ship.com.x, z: hover.ship.com.z } : this.toWorld(this.cursor.x, this.cursor.y);
    const t = safeTeleportTarget(w, p.x, p.z);
    const ctx = this.ctx;
    const [tx, ty] = this.toScreen(t.x, t.z);
    if (!hover) {
      ctx.strokeStyle = `${INK}0.8)`;
      ctx.beginPath();
      ctx.moveTo(this.cursor.x - 8, this.cursor.y);
      ctx.lineTo(this.cursor.x + 8, this.cursor.y);
      ctx.moveTo(this.cursor.x, this.cursor.y - 8);
      ctx.lineTo(this.cursor.x, this.cursor.y + 8);
      ctx.stroke();
    }
    // Her outline where she'd come up (same heading as now).
    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = COLOURS.player!;
    if (t.near && !hover) {
      ctx.beginPath();
      ctx.moveTo(this.cursor.x, this.cursor.y);
      ctx.lineTo(tx, ty);
      ctx.stroke();
    }
    ctx.translate(tx, ty);
    ctx.rotate(w.player.ship.heading);
    hullPath(ctx, 26);
    ctx.stroke();
    ctx.restore();
    if (hover) {
      const dh = Math.hypot(hover.ship.com.x - me.x, hover.ship.com.z - me.z);
      const seen = w.lookout.get(hover.id)?.visible ? 'in sight' : 'not in sight';
      const off = Math.round(Math.hypot(t.x - hover.ship.com.x, t.z - hover.ship.com.z));
      return `<b>${hover.name}</b>, ${hover.cls.name.toLowerCase()} (${hover.faction}, ${seen}) · ${bearingText(bearingTo(me.x, me.z, hover.ship.com.x, hover.ship.com.z))}, ${nmText(dh)} · ${(Math.hypot(hover.ship.vel.x, hover.ship.vel.z) * KNOTS_PER_MS).toFixed(1)} kn<br>Click to come up alongside her, ${off} m off.`;
    }
    const d = Math.hypot(t.x - me.x, t.z - me.z);
    const kept = t.near ? ` She'll come up ${Math.round(Math.hypot(t.x - t.near.ship.com.x, t.z - t.near.ship.com.z))} m off ${t.near.name}.` : '';
    const names = lost.map((v) => v.name);
    const left = names.length
      ? `<br><span class="devmap-warn">More than ${nmText(DROP_RANGE)} from ${names.length > 2 ? `${names.slice(0, 2).join(', ')} and ${names.length - 2} more` : names.join(' and ')}: they'd be dropped.</span>`
      : '';
    return `${bearingText(bearingTo(me.x, me.z, t.x, t.z))}, ${nmText(d)} from her.${kept}${left}<br>Click to move her here, heading and way as she is.`;
  }
}
